import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Channel, ChannelEPGResponse, ChannelListResponse } from '../channel.model';
import { ChannelHelper } from '../channel.helper';
import { ChannelService } from '../channel.service';
import { EPG, Program, UIEPGDirection, UIProgram } from './epg.model';
import { RemoteControlKey } from '../../remote';
import { RemoteControlService } from 'src/app/remote';

import * as fromPlayerActions from '../../player/state/player.actions';
import { PlayerState } from '../../player/state/player.reducer';
import { Store } from '@ngrx/store';

import * as moment from 'moment';

@Component({
  selector: 'app-epg',
  templateUrl: './epg.component.html',
  styleUrls: ['./epg.component.scss'],
})
export class EPGComponent implements OnDestroy {

  @Output() public sendUrlChannel = new EventEmitter();

  // Channel Data
  channels: Channel[] = [];
  channelIndex!: number;
  currentChannel?: Channel;

  // EPG Data
  channelPrograms!: Program[];
  channelProgramIndex!: number;
  liveProgram!: Program;
  liveProgramIndex!: number;
  progress!: number;

  // show EPG
  displayEPG = true;
  arrowProgramRight = true;
  arrowProgramLeft = true;
  arrowChannelUp = true;
  arrowChannelDown = true;

  setTimeoutEpg!: ReturnType<typeof setTimeout>;
  timeOut = 10;

  // The 3 programs visible at the interface (0 = left, 1 = center/middle, 2 = right)
  visiblePrograms: UIProgram[] = [];
  visibleProgramIndex!: number;

  // Show program details
  showTooltip = false;

  private remoteMonitor!: Subscription;

  constructor(
    private router: Router,
    private channelService: ChannelService,
    private remote: RemoteControlService,
    private playerStore: Store<PlayerState>,
  ) {
    // Get saved channel ID
    const currentChannelId = ChannelHelper.getSavedChannelId();
    if (currentChannelId < 1) {
      // No saved channel? Send back to home.
      this.router.navigate(['/']);
      return;
    }

    // Populate list of channels , including current channel
    this.populateChannels(currentChannelId);

    this.watchRemoteControlKeys();

    this.showEPG();
  }

  ngOnDestroy(): void {
    if (this.remoteMonitor) this.remoteMonitor.unsubscribe();
  }

  private populateChannels(currentChannelId: number): void {
    this.channelService.list().subscribe((response: ChannelListResponse) => {
      if (response && response.channels) {
        // Save list of channels
        this.channels = response.channels;
        this.channelIndex = response.channels.findIndex(c => c.ID === currentChannelId);
        this.currentChannel = response.channels.find(c => c.ID === currentChannelId);

        if (!this.currentChannel) {
          console.warn(`No channel with id ${currentChannelId} found on list of channels`);
          this.router.navigate(['/']);
          return;
        }
        this.populatePrograms(this.currentChannel?.Slug, this.currentChannel.Url);
      } else {
        console.warn(`No channels or channel with id ${currentChannelId} found`);
        this.router.navigate(['/']);
        return;
      }
    });
  }

  private populatePrograms(channelSlug: string, wacthUrlChannel: string): void {
    this.channelService.listProgram(channelSlug).subscribe((response: ChannelEPGResponse) => {
      if (response && response.programs) {
        this.channelPrograms = response.programs;

        this.liveProgramIndex = this.channelPrograms.findIndex(p => this.isProgramLive(p));
        this.liveProgram = this.channelPrograms[this.liveProgramIndex];

        // Decide which program to display
        this.pickPrograms(UIEPGDirection.Initial);
      }
    });
    ChannelHelper.saveLastChannelId(this.currentChannel?.ID || 0);
    this.getUrlChannel(wacthUrlChannel);
  }

  private isProgramLive(program: Program): boolean {

    // TODO: instalar e utilizar moment
    // TODO: descobrir a hora atual
    // TODO: descobrir se a hora atual esta entre o inicio e o fim do programa.
    const nowTime = '2022-07-03T'+moment().format('HH:MM')//moment().format();
    // console.log(`hora atual ${nowTime}`)
    this.progressProgram(program);

    return moment(nowTime).isBetween(program.StartTime, program.EndTime, 'minutes', '[]');
  }
  // calcula o progresso de programa
  private progressProgram(program: Program): void{
    this.progress = 100 * moment.duration(moment().diff(moment(program.StartTime))).minutes() /
                    moment.duration(moment(program.EndTime).diff(moment(program.StartTime))).minutes();
  }

  private pickPrograms(direction: UIEPGDirection): void {
    // Calculate Indexes
    switch (direction) {
      case UIEPGDirection.Left:
        if (this.visibleProgramIndex === 0) {
          if (this.channelProgramIndex > 0) this.channelProgramIndex--;
        } else {
          this.visibleProgramIndex--;
        }
        break;
      case UIEPGDirection.Right:
        if (this.visibleProgramIndex === 2) {
          if (this.channelProgramIndex < this.channelPrograms.length - 3) this.channelProgramIndex++;
        } else {
          this.visibleProgramIndex++;
        }
        break;
      default:
        this.channelProgramIndex = this.liveProgramIndex;
        this.visibleProgramIndex = 0;
    }

    this.showArrow(direction);

    // Populate visible programs
    this.visiblePrograms = this.channelPrograms.slice(this.channelProgramIndex, this.channelProgramIndex + 3).map(program => {
      const uiProgram: UIProgram = { isLive: (program === this.liveProgram), isWatching: false, program };
      return uiProgram;
    });

    // Decide when to PIN live at the left side of the epg list
    if (this.visibleProgramIndex <= 2 && this.liveProgramIndex < this.channelProgramIndex) {
      this.visiblePrograms[0] = { isLive: true, isWatching: false, program: this.liveProgram };
      return;
    }

    // Decide when to PIN live at the right side of the epg list;
    if (this.visibleProgramIndex >= 0 && this.liveProgramIndex > this.channelProgramIndex) {
      if (this.liveProgramIndex - this.channelProgramIndex < 3) {
        this.visiblePrograms[this.liveProgramIndex - this.channelProgramIndex] = {
          isLive: true, isWatching: false, program: this.liveProgram
        };
        return;
      } else {
        this.visiblePrograms[2] = { isLive: true, isWatching: false, program: this.liveProgram };
      }
      return;
    }

  }

  private pickChannel(direction: number): void{
    switch (direction) {
      case UIEPGDirection.Down:
        this.channelIndex = this.channelIndex + 1;
        if (this.channelIndex < this.channels.length) {
          this.currentChannel = this.channels[this.channelIndex];
          this.populatePrograms(this.currentChannel?.Slug, this.currentChannel.Url);
        } else {
          this.channelIndex = this.channels.length - 1;
        }
        break;
      case UIEPGDirection.Up:
        this.channelIndex = this.channelIndex - 1;
        if (this.channelIndex >= 0) {
          this.currentChannel = this.channels[this.channelIndex];
          this.populatePrograms(this.currentChannel?.Slug, this.currentChannel.Url);
        } else {
          this.channelIndex = 0;
        }
        break;
      default:
        this.currentChannel = this.channels[this.channelIndex];
        this.populatePrograms(this.currentChannel?.Slug, this.currentChannel.Url);
    }
    this.showArrow(direction);
  }

  showEPG(): void {
    if (this.displayEPG === false) this.displayEPG = true;
    // if (this.showTooltip) this.showTooltip = false;
    this.setTimeoutEpg = setTimeout(() => {this.displayEPG = false; }, this.timeOut * 1000);
  }

  public getUrlChannel(event: string): void{
    this.sendUrlChannel.emit(event);
  }

  private showArrow(direction: UIEPGDirection): void {
    // TODO melhora logica
    if ( direction === UIEPGDirection.Left && this.channelProgramIndex === 0){
      this.arrowProgramLeft = false;
      return;
    }else{
      this.arrowProgramLeft = true;
    }

    if (direction === UIEPGDirection.Right && this.channelProgramIndex === this.channelPrograms.length - 3){
      this.arrowProgramRight = false;
      return;
    }else{
      this.arrowProgramRight = true;
    }

    if (this.channelIndex + 1 >= this.channels.length){
      this.arrowChannelDown = false;
      return;
    }else{
      this.arrowChannelDown = true;
    }

    if (this.channelIndex <= 0){
      this.arrowChannelUp = false;
      return;
    }else{
      this.arrowChannelUp = true;
    }
  }

  private watchRemoteControlKeys(): void {
    this.remoteMonitor = this.remote.OnKeyPress.subscribe((re) => {

      clearTimeout(this.setTimeoutEpg);
      this.showEPG();

      if (re.RemoteKey === RemoteControlKey[RemoteControlKey.Right]) {
        this.pickPrograms(UIEPGDirection.Right);
        this.showTooltip = false;
        return;
      }

      if (re.RemoteKey === RemoteControlKey[RemoteControlKey.Left]) {
        this.pickPrograms(UIEPGDirection.Left);
        this.showTooltip = false;
      }

      if (re.RemoteKey === RemoteControlKey[RemoteControlKey.Up]) {
        this.pickChannel(UIEPGDirection.Up);
        this.showTooltip = false;
        return;
      }

      if (re.RemoteKey === RemoteControlKey[RemoteControlKey.Down]) {
        this.pickChannel(UIEPGDirection.Down);
        this.showTooltip = false;
        return;
      }

      // Show/Hide Program Info
      if (re.RemoteKey === RemoteControlKey[RemoteControlKey.P] || re.RemoteKey === RemoteControlKey[RemoteControlKey.RedButton] ) {
        this.showTooltip = !this.showTooltip;
      }

      // TODO: see if its possible to have back implemented globally
      if (
        re.RemoteKey === RemoteControlKey[RemoteControlKey.Back] ||
        re.RemoteKey === RemoteControlKey[RemoteControlKey.Backspace]
      ) {
        this.router.navigate(['/home']);
      }
    });
  }
}
